import { 
  Component, Input, Output, EventEmitter, TemplateRef, ContentChild, 
  ViewChild, OnInit, OnChanges, OnDestroy, AfterViewInit, SimpleChanges,
  booleanAttribute, numberAttribute, ChangeDetectorRef, ElementRef, HostListener
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CdkDragDrop, CdkDropList, CdkDrag, CdkDragHandle, moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import * as XLSX from 'xlsx';
import { MatInputModule } from '@angular/material/input';

export interface TableColumn {
  key: string;
  title: string;
  sortable?: boolean;
  visible?: boolean;
  width?: number;
  cellTemplate?: TemplateRef<any>;
  headerTooltip?: string;
  align?: 'left' | 'center' | 'right';
  format?: (value: any, row?: any) => any;
  filterable?: boolean;
  headerIcon?: string;
  pinned?: boolean; // Column will be fixed on the left side
  clickable?: boolean;
  onClick?: (row: any) => void;
}

@Component({
  selector: 'app-custom-table-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTooltipModule,
    DragDropModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,

    MatInputModule,

  ],
  templateUrl: './custom-table-view.component.html',
  styleUrls: ['./custom-table-view.component.scss']
})
export class CustomTableViewComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private static readonly DEFAULT_COLUMN_WIDTH = 160;
  private static readonly DEFAULT_ACTIONS_COLUMN_WIDTH = 72;
  // Input properties
  Math = Math;
  readonly defaultColumnWidth = CustomTableViewComponent.DEFAULT_COLUMN_WIDTH;
  @Input({ required: true }) columns: TableColumn[] = [];
  @Input({ required: true }) data: any[] = [];
  @Input({ transform: booleanAttribute }) showSearch = true;
  @Input({ transform: booleanAttribute }) showRefresh = true;
  @Input({ transform: booleanAttribute }) showColumnToggle = true;
  @Input({ transform: booleanAttribute }) showExport = true;
  @Input({ transform: booleanAttribute }) showPagination = true;
  @Input({ transform: booleanAttribute }) showColumnFilters = true;
  @Input({ transform: booleanAttribute }) showColumnResize = true;
  @Input({ transform: booleanAttribute }) rowClickable = true;
  @Input({ transform: booleanAttribute }) showDefaultActions = true;
  @Input({ transform: numberAttribute }) itemsPerPage = 50;
  @Input({ transform: numberAttribute }) currentPage = 1;
  @Input() pageSizeOptions: number[] = [50, 100, 250, 500, 800, 1000];
  /** When true, data is already paged by the server; footer uses totalCount and emits pageChange. */
  @Input({ transform: booleanAttribute }) serverSidePagination = false;
  @Input({ transform: numberAttribute }) totalCount = 0;
  @Input() noDataMessage = 'No data matching the filters.';
  @Input() exportFileName = 'table_export.xlsx';
  @Input() selectionMode: 'none' | 'single' | 'multiple' = 'none';
  @Input({ transform: booleanAttribute }) loading = false;
  /** Max number of columns that can be pinned by dragging. 0 = no limit (one-by-one: drag to left to add pinned, drag out to remove). Set > 0 to cap (e.g. 10). Negative = disable drag-to-pin. */
  @Input({ transform: numberAttribute }) pinnedZoneCount = 0;
  /** Gap from viewport bottom when sizing sticky table layout (px). */
  @Input({ transform: numberAttribute }) viewportBottomGap = 16;
  
  // Template inputs
  @Input() actionsTemplate?: TemplateRef<any>;
  @Input() headerTemplate?: TemplateRef<any>;
  @Input() footerTemplate?: TemplateRef<any>;
  @Input() rowClass?: (row: any) => string; // Function to return CSS class for row
  
  // Output events
  @Output() rowClick = new EventEmitter<any>();
  @Output() rowDblClick = new EventEmitter<any>();
  @Output() editRow = new EventEmitter<any>();
  @Output() deleteRow = new EventEmitter<any>();
  @Output() export = new EventEmitter<any[]>();
  @Output() refresh = new EventEmitter<void>();
  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() columnVisibilityChange = new EventEmitter<TableColumn[]>();
  @Output() pageChange = new EventEmitter<{ pageIndex: number; pageSize: number; offset: number }>();

  // Internal state
  allColumns: TableColumn[] = [];
  visibleColumns: TableColumn[] = [];
  pinnedColumns: TableColumn[] = [];
  scrollableColumns: TableColumn[] = [];
  displayedColumns: string[] = [];
  filteredAndSortedData: any[] = [];
  globalFilter = '';
  columnFilters: { [key: string]: string } = {};
  currentSortColumn = '';
  sortDirection: 'asc' | 'desc' | '' = '';
  totalItems = 0;
  filteredItemCount = 0;
  totalRecordCount = 0;
  hasActiveFilters = false;
  isResizing = false;
  selectedRows: any[] = [];
  filtersVisible = false; // Toggle state for column filters visibility (hidden by default)
  
  // Column resizing
  private resizingColumn: TableColumn | null = null;
  private startX = 0;
  private startWidth = 0;
  private resizeObserver?: ResizeObserver;
  /** Viewport top offset captured once per layout pass; prevents table growing when page scrolls. */
  private measuredTopOffset: number | null = null;

  @ViewChild('tableContainer') tableContainer?: ElementRef<HTMLElement>;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.initializeColumns();
    this.applyFiltersAndSort();
  }

  ngAfterViewInit(): void {
    this.updateStickyLayoutHeight(true);
    this.resizeObserver = new ResizeObserver(() => this.updateStickyLayoutHeight(true));
    if (this.tableContainer?.nativeElement) {
      this.resizeObserver.observe(this.tableContainer.nativeElement);
    }
    let parent: HTMLElement | null = this.tableContainer?.nativeElement.parentElement ?? null;
    while (parent) {
      this.resizeObserver.observe(parent);
      if (parent.classList.contains('main-content')) {
        break;
      }
      parent = parent.parentElement;
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateStickyLayoutHeight(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns'] || changes['actionsTemplate'] || changes['showDefaultActions']) {
      this.initializeColumns();
    }
    if (changes['data'] || changes['selectionMode'] || changes['totalCount'] || changes['currentPage']) {
      this.applyFiltersAndSort();
    }
    if (changes['data'] || changes['loading'] || changes['showPagination'] || changes['filtersVisible']) {
      setTimeout(() => this.updateStickyLayoutHeight(false));
    }
  }

  private updateStickyLayoutHeight(forceRemeasure = false): void {
    const container = this.tableContainer?.nativeElement;
    if (!container || typeof window === 'undefined') {
      return;
    }

    if (forceRemeasure || this.measuredTopOffset === null) {
      this.measuredTopOffset = container.getBoundingClientRect().top;
    }

    const mainContent = document.querySelector('.main-content') as HTMLElement | null;
    let bottomLimit = window.innerHeight - this.viewportBottomGap;
    if (mainContent) {
      const mainRect = mainContent.getBoundingClientRect();
      bottomLimit = Math.min(bottomLimit, mainRect.bottom - this.viewportBottomGap);
    }

    const height = Math.max(280, Math.floor(bottomLimit - this.measuredTopOffset));
    container.style.setProperty('--custom-table-height', `${height}px`);
    container.style.height = `${height}px`;
    container.style.maxHeight = `${height}px`;
    container.style.minHeight = `${height}px`;
    this.updateTheadHeight();
  }

  private updateTheadHeight(): void {
    const container = this.tableContainer?.nativeElement;
    if (!container) {
      return;
    }

    const thead = container.querySelector('.table-wrapper thead') as HTMLElement | null;
    const wrapper = container.querySelector('.table-wrapper') as HTMLElement | null;
    if (thead && wrapper) {
      wrapper.style.setProperty(
        '--custom-table-thead-height',
        `${Math.ceil(thead.getBoundingClientRect().height)}px`
      );
    }
  }

  private isActionsColumnKey(key: string): boolean {
    return key === 'actions' || key === 'action';
  }

  private buildActionsColumn(): TableColumn {
    return {
      key: 'actions',
      title: 'Actions',
      visible: true,
      sortable: false,
      filterable: false,
      width: CustomTableViewComponent.DEFAULT_ACTIONS_COLUMN_WIDTH,
      align: 'left' as const
    };
  }

  initializeColumns(): void {
    const hasCheckbox = this.columns.some(col => col.key === 'checkbox');
    const hasActionsColumn = this.columns.some(col => this.isActionsColumnKey(col.key));
    const shouldAddActionsColumn =
      !hasActionsColumn && (this.showDefaultActions || !!this.actionsTemplate);

    const mappedColumns = this.columns.map(col => {
      const key = col.key === 'action' ? 'actions' : col.key;
      const width =
        this.isActionsColumnKey(key)
          ? col.width ?? CustomTableViewComponent.DEFAULT_ACTIONS_COLUMN_WIDTH
          : col.width ?? CustomTableViewComponent.DEFAULT_COLUMN_WIDTH;
      return {
        ...col,
        key,
        visible: col.visible !== false,
        sortable: col.sortable !== false,
        pinned: col.pinned === true,
        align: (col.align || (this.isActionsColumnKey(key) ? 'left' : 'left')) as 'left' | 'center' | 'right',
        width,
        filterable: this.isActionsColumnKey(key) ? col.filterable ?? false : col.filterable
      };
    });

    this.allColumns = [
      ...(this.selectionMode !== 'none' && !hasCheckbox
        ? [{
            key: 'checkbox',
            title: '',
            visible: true,
            width: 50,
            sortable: false,
            align: 'center' as const
          }]
        : []),
      ...mappedColumns,
      ...(shouldAddActionsColumn ? [this.buildActionsColumn()] : [])
    ];

    this.updateDisplayedColumns();
  }

  updateDisplayedColumns(): void {
    this.visibleColumns = this.allColumns.filter(col => col.visible);
    this.displayedColumns = this.visibleColumns.map(col => col.key);
    
    // Separate pinned and scrollable columns
    this.pinnedColumns = this.visibleColumns.filter(col => col.pinned === true);
    this.scrollableColumns = this.visibleColumns.filter(col => col.pinned !== true);
    
    this.applyFiltersAndSort();
    this.columnVisibilityChange.emit(this.allColumns);
  }

  get pageSize(): number {
    return Number(this.itemsPerPage) || 50;
  }

  get hasPreviousPage(): boolean {
    return this.currentPage > 1;
  }

  get hasNextPage(): boolean {
    if (this.serverSidePagination) {
      if (this.totalCount <= 0) {
        return false;
      }
      return this.currentPage * this.pageSize < this.totalCount;
    }

    const total = this.hasActiveFilters ? this.filteredItemCount : this.totalRecordCount;
    if (total <= 0) {
      return false;
    }
    return this.currentPage * this.pageSize < total;
  }

  get pageRangeStart(): number {
    if (this.serverSidePagination) {
      if (this.totalCount === 0 || this.paginatedData.length === 0) {
        return 0;
      }
      return (this.currentPage - 1) * this.pageSize + 1;
    }
    if (this.filteredItemCount === 0) {
      return 0;
    }
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get pageRangeEnd(): number {
    if (this.serverSidePagination) {
      if (this.totalCount === 0 || this.paginatedData.length === 0) {
        return 0;
      }
      return this.pageRangeStart + this.paginatedData.length - 1;
    }
    if (this.filteredItemCount === 0) {
      return 0;
    }
    return Math.min(this.currentPage * this.pageSize, this.filteredItemCount);
  }

  get paginatedData(): any[] {
    if (!this.showPagination || this.serverSidePagination) {
      return this.filteredAndSortedData;
    }
    return this.filteredAndSortedData.slice(
      (this.currentPage - 1) * this.itemsPerPage,
      this.currentPage * this.itemsPerPage
    );
  }

  onSearch(event: Event): void {
    this.globalFilter = (event.target as HTMLInputElement).value.toLowerCase();
    this.currentPage = 1;
    this.applyFiltersAndSort();
  }

  applyColumnFilter(event: Event, columnKey: string): void {
    this.columnFilters[columnKey] = (event.target as HTMLInputElement).value.toLowerCase();
    this.currentPage = 1;
    this.applyFiltersAndSort();
  }

  applyFiltersAndSort(): void {
    let filteredData = this.data.filter(row => {
      if (this.globalFilter) {
        const globalMatch = this.allColumns.some(col => {
          // Skip columns that are not filterable or are special columns
          if (col.key === 'checkbox' || col.key === 'actions' || col.filterable === false) return false;
          
          // Get raw value first
          const rawValue = this.getRowValue(row, col.key);
          if (rawValue === null || rawValue === undefined) return false;
          
          // Try formatted value if format function exists, otherwise use raw value
          let searchValue: string;
          try {
            const formattedValue = this.getFormattedValue(row, col.key);
            searchValue = String(formattedValue || rawValue).toLowerCase();
          } catch (e) {
            searchValue = String(rawValue).toLowerCase();
          }
          
          return searchValue.includes(this.globalFilter);
        });
        if (!globalMatch) return false;
      }

      for (const key in this.columnFilters) {
        if (this.columnFilters[key]) {
          const rawValue = this.getRowValue(row, key);
          const formattedValue = this.getFormattedValue(row, key);
          const columnValue = String(formattedValue || rawValue || '').toLowerCase();
          if (!columnValue.includes(this.columnFilters[key])) return false;
        }
      }
      return true;
    });

    if (this.currentSortColumn) {
      filteredData.sort((a, b) => {
        const aValue = this.getFormattedValue(a, this.currentSortColumn);
        const bValue = this.getFormattedValue(b, this.currentSortColumn);

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return this.sortDirection === 'asc' ? 
            aValue.localeCompare(bValue) : 
            bValue.localeCompare(aValue);
        }
        return this.sortDirection === 'asc' ? 
          (aValue < bValue ? -1 : 1) : 
          (aValue < bValue ? 1 : -1);
      });
    }

    this.filteredAndSortedData = filteredData;
    this.hasActiveFilters =
      !!this.globalFilter ||
      Object.values(this.columnFilters).some((value) => !!value);
    this.totalRecordCount = this.serverSidePagination ? this.totalCount : this.data.length;
    this.filteredItemCount = this.filteredAndSortedData.length;
    this.totalItems = this.serverSidePagination
      ? this.totalCount
      : this.filteredItemCount;

    if (!this.serverSidePagination) {
      const maxPage = Math.max(1, Math.ceil(this.filteredItemCount / this.pageSize));
      if (this.currentPage > maxPage) {
        this.currentPage = maxPage;
      }
    } else if (this.totalCount <= 0) {
      this.currentPage = 1;
    } else {
      const maxPage = Math.max(1, Math.ceil(this.totalCount / this.pageSize));
      if (this.currentPage > maxPage) {
        this.currentPage = maxPage;
      }
    }
    
    if (this.selectionMode !== 'none') {
      this.selectedRows = [];
      this.selectionChange.emit(this.selectedRows);
    }
  }

  onSort(column: TableColumn): void {
    if (column.sortable) {
      if (this.currentSortColumn === column.key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 
                            (this.sortDirection === 'desc' ? '' : 'asc');
      } else {
        this.currentSortColumn = column.key;
        this.sortDirection = 'asc';
      }
      this.currentPage = 1;
      this.applyFiltersAndSort();
    }
  }

  getRowValue(row: any, key: string): any {
    return key.split('.').reduce((o, i) => o?.[i], row);
  }

  getFormattedValue(row: any, key: string): any {
    const column = this.allColumns.find(col => col.key === key);
    const value = this.getRowValue(row, key);
    return column?.format ? column.format(value, row) : value;
  }

  onColumnDrop(event: CdkDragDrop<TableColumn[]>): void {
    if (this.pinnedZoneCount < 0) return; // negative = disable drag-to-pin
    const visibleOrder = this.allColumns.filter(c => c.visible);
    const invisible = this.allColumns.filter(c => !c.visible);
    const droppedColumn = visibleOrder[event.previousIndex];
    const wasPinned = droppedColumn.pinned;
    const pinnableOrderBefore = visibleOrder.filter(
      c => c.key !== 'checkbox' && c.key !== 'actions'
    );
    const previousPinnedCount = pinnableOrderBefore.filter(c => c.pinned).length;

    moveItemInArray(visibleOrder, event.previousIndex, event.currentIndex);
    this.allColumns = [...visibleOrder, ...invisible];

    const pinnableOrderAfter = visibleOrder.filter(
      c => c.key !== 'checkbox' && c.key !== 'actions'
    );
    const indexInPinnableAfter = pinnableOrderAfter.indexOf(droppedColumn);

    let newPinnedCount: number;
    if (!wasPinned && indexInPinnableAfter <= previousPinnedCount) {
      newPinnedCount = previousPinnedCount + 1;
    } else if (wasPinned && indexInPinnableAfter >= previousPinnedCount) {
      newPinnedCount = Math.max(0, previousPinnedCount - 1);
    } else {
      newPinnedCount = previousPinnedCount;
    }
    if (this.pinnedZoneCount > 0) {
      newPinnedCount = Math.min(newPinnedCount, this.pinnedZoneCount);
    }

    pinnableOrderAfter.forEach((col, idx) => {
      col.pinned = idx < newPinnedCount;
    });

    this.updateDisplayedColumns();
    this.cdr.detectChanges();
  }

  /**
   * Checks if a column should be included in Excel export:
   * 1. Must be currently visible (present).
   * 2. Must not be a control column (checkbox, actions, action).
   * 3. Must not be an ID mentioned column (key or title represents an ID).
   */
  isExportableColumn(col: TableColumn): boolean {
    if (!col || col.visible === false) {
      return false;
    }
    const key = (col.key || '').trim();
    const keyLower = key.toLowerCase();
    const title = (col.title || '').trim();
    const titleLower = title.toLowerCase();

    // Exclude control / special columns
    if (keyLower === 'checkbox' || keyLower === 'actions' || keyLower === 'action') {
      return false;
    }

    // Exclude ID mentioned columns (key or title indicates an ID field)
    if (
      keyLower === 'id' ||
      keyLower === '_id' ||
      keyLower.endsWith('_id') ||
      keyLower.startsWith('id_') ||
      keyLower.startsWith('_id') ||
      /[a-z0-9]Id$/.test(key) ||
      /\bid\b/i.test(titleLower) ||
      /\bid\b/i.test(keyLower) ||
      titleLower === 'id' ||
      titleLower === 'id.' ||
      titleLower.endsWith(' id') ||
      titleLower.endsWith(' id.') ||
      titleLower.startsWith('id ') ||
      titleLower.startsWith('id. ') ||
      titleLower.includes('id mentioned')
    ) {
      return false;
    }

    return true;
  }

  getExportData(): any[] {
    const exportableCols = this.visibleColumns.filter(col => this.isExportableColumn(col));

    return this.filteredAndSortedData.map(row => {
      const exportRow: Record<string, any> = {};
      exportableCols.forEach(col => {
        const header = col.title || col.key;
        let val = this.getFormattedValue(row, col.key);
        if (val === null || val === undefined) {
          val = '';
        } else if (typeof val === 'string' && /<[^>]*>/.test(val)) {
          val = val.replace(/<[^>]*>/g, '').trim();
        }
        exportRow[header] = val;
      });
      return exportRow;
    });
  }

  onExport(): void {
    const exportRows = this.getExportData();
    if (this.export.observers.length > 0) {
      this.export.emit(this.filteredAndSortedData);
    } else {
      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportRows);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(wb, this.exportFileName);
    }
  }

  onRefresh(): void {
    console.log('Custom table refresh button clicked - emitting refresh event');
    this.refresh.emit();
  }

  onRowClick(row: any): void {
    if (this.rowClickable && !this.loading) {
      this.rowClick.emit(row);
    }
    
    if (this.selectionMode === 'single') {
      this.selectedRows = [row];
      this.selectionChange.emit(this.selectedRows);
    }
  }

  onRowDblClick(row: any): void {
    if (this.rowClickable && !this.loading) {
      this.rowDblClick.emit(row);
    }
  }

  onRowSelect(row: any, event: Event): void {
    event.stopPropagation();
    
    if (this.selectionMode === 'multiple') {
      const index = this.selectedRows.findIndex(r => this.compareRows(r, row));
      if (index >= 0) {
        this.selectedRows.splice(index, 1);
      } else {
        this.selectedRows.push(row);
      }
      this.selectionChange.emit(this.selectedRows);
    }
  }

  onSelectAll(event: Event): void {
    event.stopPropagation();
    
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      this.selectedRows = [...this.paginatedData];
    } else {
      this.selectedRows = [];
    }
    this.selectionChange.emit(this.selectedRows);
  }

  onEditRow(row: any, event?: Event): void {
    event?.stopPropagation();
    this.editRow.emit(row);
  }

  onDeleteRow(row: any, event?: Event): void {
    event?.stopPropagation();
    this.deleteRow.emit(row);
  }

  onItemsPerPageChange(): void {
    this.itemsPerPage = Number(this.itemsPerPage);
    this.currentPage = 1;
    if (this.serverSidePagination) {
      this.emitPageChange();
    } else {
      this.applyFiltersAndSort();
    }
  }

  resetPagination(): void {
    this.currentPage = 1;
  }

  private emitPageChange(): void {
    this.pageChange.emit({
      pageIndex: this.currentPage - 1,
      pageSize: this.itemsPerPage,
      offset: (this.currentPage - 1) * this.itemsPerPage
    });
  }

  toggleFilters(): void {
    this.filtersVisible = !this.filtersVisible;
    setTimeout(() => this.updateTheadHeight());
  }

  /** Show column filters (if hidden) and focus the filter input for a column. */
  focusColumnFilter(columnKey: string): void {
    if (!this.filtersVisible) {
      this.filtersVisible = true;
    }
    setTimeout(() => {
      const el = document.getElementById('col-filter-' + columnKey);
      if (el) (el as HTMLInputElement).focus();
    }, 0);
  }

  /** Column header filter icon: when filters visible, hide them; otherwise show and focus. */
  onColumnFilterIconClick(columnKey: string): void {
    if (this.filtersVisible) {
      this.toggleFilters();
    } else {
      this.focusColumnFilter(columnKey);
    }
  }

  toggleColumnPin(column: TableColumn): void {
    column.pinned = !column.pinned;
    this.updateDisplayedColumns();
  }

  getPinnedColumnsWidth(): number {
    return this.pinnedColumns.reduce((total, col) => total + (col.width ?? CustomTableViewComponent.DEFAULT_COLUMN_WIDTH), 0);
  }

  getPinnedColumnLeft(column: TableColumn): number {
    if (!column.pinned) return 0;
    let left = 0;
    for (const col of this.visibleColumns) {
      if (col === column) break;
      if (col.pinned) {
        left += (col.width ?? CustomTableViewComponent.DEFAULT_COLUMN_WIDTH);
      }
    }
    return left;
  }

  /** trackBy for column ngFor so header/cell bindings stay correct after drag reorder */
  trackByColumnKey(_index: number, col: TableColumn): string {
    return col.key;
  }

  previousPage(): void {
    if (!this.hasPreviousPage) {
      return;
    }
    this.currentPage--;
    if (this.serverSidePagination) {
      this.emitPageChange();
    }
  }

  nextPage(): void {
    if (!this.hasNextPage) {
      return;
    }
    this.currentPage++;
    if (this.serverSidePagination) {
      this.emitPageChange();
    }
  }

  onResizeStart(event: MouseEvent, column: TableColumn): void {
    event.preventDefault();
    this.resizingColumn = column;
    this.startX = event.pageX;
    this.startWidth = column.width ?? CustomTableViewComponent.DEFAULT_COLUMN_WIDTH;
    this.isResizing = true;

    document.addEventListener('mousemove', this.onResize);
    document.addEventListener('mouseup', this.onResizeEnd);
  }

  private onResize = (event: MouseEvent): void => {
    if (this.resizingColumn) {
      const diff = event.pageX - this.startX;
      this.resizingColumn.width = Math.max(50, this.startWidth + diff);
    }
  }

  private onResizeEnd = (): void => {
    this.resizingColumn = null;
    this.isResizing = false;
    document.removeEventListener('mousemove', this.onResize);
    document.removeEventListener('mouseup', this.onResizeEnd);
  }

  private compareRows(a: any, b: any): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  isRowSelected(row: any): boolean {
    return this.selectedRows.some(selectedRow => 
      this.compareRows(selectedRow, row)
    );
  }
}